import { FormEvent, useEffect, useState } from "react";
import { Blog, listBlogs, generateBlog } from "../lib/api";
import BlogCard from "../components/BlogCard";
import CostWidget from "../components/CostWidget";
import Button from "../ui/Button";
import { useAsyncAction } from "../ui/useAsyncAction";
import { IconAlertCircle, IconPlus, IconSparkles } from "../ui/icons";

const FILTERS = [
  { key: "", label: "All" },
  { key: "awaiting_approval", label: "Needs review" },
  { key: "approved", label: "Approved" },
  { key: "published", label: "Published" },
  { key: "failed", label: "Failed" },
];

export default function Dashboard() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [filter, setFilter] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [hint, setHint] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      setBlogs(await listBlogs(filter || undefined));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [filter]);

  const kickoff = useAsyncAction(
    async (topicHint: string | undefined) => {
      await generateBlog(topicHint);
    },
    {
      loadingMessage: "Starting pipeline…",
      successMessage: "Blog pipeline started",
      successDescription: "It'll move through research → draft → image → review.",
      onSuccess: async () => {
        setShowGenerate(false);
        setHint("");
        await load();
      },
    }
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    kickoff.run(hint || undefined);
  };

  const filterCounts: Record<string, number> = {};
  for (const b of blogs) filterCounts[b.status] = (filterCounts[b.status] ?? 0) + 1;

  return (
    <div>
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-800 tracking-tight">
            Content queue
          </h1>
          <p className="text-sm text-ink-500 mt-1.5">
            Blogs auto-generate every Monday. Or kick one off now.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <CostWidget />
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowGenerate((v) => !v)}
            iconLeft={<IconPlus size={15} />}
          >
            Generate new blog
          </Button>
        </div>
      </header>

      {showGenerate && (
        <form
          onSubmit={onSubmit}
          className="mb-6 bg-white border border-ink-100 rounded-xl p-5 shadow-sm shadow-ink-900/5"
        >
          <label className="flex items-center gap-2 text-sm font-medium text-ink-800 mb-2">
            <IconSparkles size={14} className="text-brand-500" />
            Optional topic hint
          </label>
          <input
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            className="w-full px-3 py-2 border border-ink-200 rounded-md text-sm mb-3 focus:border-brand-500"
            placeholder="e.g. benefits of NAD+ for over-40s"
          />
          <p className="text-xs text-ink-500 mb-3">
            Leave empty to let the system choose a high-intent topic from your
            keyword universe.
          </p>
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              pending={kickoff.pending}
              iconLeft={<IconSparkles size={14} />}
            >
              {kickoff.pending ? "Starting…" : "Start pipeline"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowGenerate(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="flex gap-1.5 mb-5 flex-wrap">
        {FILTERS.map((f) => {
          const selected = filter === f.key;
          const count = f.key
            ? filterCounts[f.key] ?? 0
            : blogs.length;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={[
                "inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-full",
                "border transition-colors",
                selected
                  ? "bg-ink-800 text-white border-ink-800"
                  : "bg-white text-ink-700 border-ink-200 hover:border-ink-300 hover:bg-ink-50",
              ].join(" ")}
            >
              {f.label}
              {filter === f.key || count > 0 ? (
                <span
                  className={[
                    "tabular-nums text-xs px-1.5 rounded-full",
                    selected ? "bg-white/20" : "bg-ink-100 text-ink-600",
                  ].join(" ")}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2.5">
          <IconAlertCircle size={16} className="text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{err}</p>
        </div>
      )}

      {blogs.length === 0 ? (
        <EmptyState onGenerate={() => setShowGenerate(true)} />
      ) : (
        <div className="grid gap-3">
          {blogs.map((b) => (
            <BlogCard key={b.id} blog={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onGenerate }: { onGenerate: () => void }) {
  return (
    <div className="bg-white border border-ink-100 rounded-xl p-12 text-center">
      <div className="mx-auto h-14 w-14 mb-4 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center">
        <IconSparkles size={22} />
      </div>
      <h3 className="font-display text-xl font-semibold text-ink-800 mb-1">
        No blogs yet
      </h3>
      <p className="text-sm text-ink-500 mb-5">
        Start your first blog pipeline — research, draft, image, and review all
        happen automatically.
      </p>
      <Button
        variant="primary"
        onClick={onGenerate}
        iconLeft={<IconPlus size={14} />}
      >
        Generate new blog
      </Button>
    </div>
  );
}
